import React, { useEffect, useState } from "react";
import ProfileHeader from "./ProfileHeader";
import ProfileTabs from "./ProfileTabs";
import { ProfileData } from "@/hooks/useProfiles";
import { getProfileUrl } from "@/utils/profileRedirects";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Clock } from "lucide-react";
import SEOHead from "../seo/SEOHead";
import { getSocialMediaSafeImageUrl } from "@/utils/imageUtils";

interface ProfilePageContentProps {
  profile: ProfileData & {
    derivedProfession?: string | null;
  };
  placeholderImage: string;
  isSimplifiedView?: boolean;
  structuredData?: Record<string, any>;
  onOpenHireMeModal: () => void;
  onOpenPrimaryHireMeModal: () => void;
  onSetHireMeScrollPopupVisible: (visible: boolean) => void;
  openPortfolioModal?: any;
  onClosePortfolioModal?: () => void;
}
const ProfilePageContent: React.FC<ProfilePageContentProps> = ({
  profile,
  placeholderImage,
  isSimplifiedView = false,
  structuredData,
  onOpenHireMeModal,
  onOpenPrimaryHireMeModal,
  onSetHireMeScrollPopupVisible,
  openPortfolioModal,
  onClosePortfolioModal,
}) => {
  const profession = profile.derivedProfession || profile.profession || "Designer";
  const location = profile.location || "Africa";
  const displayName = profile.display_name || profile.username || "Anonymous Designer";
  const pageTitle = `${displayName} - Top ${profession} from ${location} | AfricansWhoDesign`;
  const pageDescription = profile.bio || `${displayName} is a ${location}-based top ${profession}. View portfolio, experience, and projects. Connect for collaborations and opportunities. Hire the best.`;
  const canonicalUrl = `${window.location.origin}${getProfileUrl(profile.id, profile.username_slug)}`;

  // Process image URL once with proper fallback logic
  const socialMediaImageUrl = profile.avatar_url 
    ? getSocialMediaSafeImageUrl(profile.avatar_url) || placeholderImage
    : placeholderImage;

  // Debug logging for social media image URL
  console.log('[ProfilePageContent] Social media image URL:', {
    original: profile.avatar_url,
    processed: socialMediaImageUrl,
    isPlaceholder: socialMediaImageUrl === placeholderImage
  });

  useEffect(() => {
    let lastScrollY = window.scrollY;
    let hideTimeout: NodeJS.Timeout | null = null;
    const handleScroll = () => {
      const scrollY = window.scrollY;
      const pageHeight = document.body.scrollHeight - window.innerHeight;
      const scrollPercentage = scrollY / pageHeight;

      if (scrollPercentage > 0.5) {
        if (scrollY > lastScrollY) {
          // Scrolling down
          if (hideTimeout) {
            clearTimeout(hideTimeout);
            hideTimeout = null;
          }
          onSetHireMeScrollPopupVisible(true);
        } else if (scrollY < lastScrollY) {
          // Scrolling up
          if (!hideTimeout) {
            hideTimeout = setTimeout(() => {
              onSetHireMeScrollPopupVisible(false);
              hideTimeout = null;
            }, 400);
          }
        }
      } else {
        if (hideTimeout) {
          clearTimeout(hideTimeout);
          hideTimeout = null;
        }
        onSetHireMeScrollPopupVisible(false);
      }
      lastScrollY = scrollY;
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (hideTimeout) clearTimeout(hideTimeout);
    };
  }, [onSetHireMeScrollPopupVisible]);

  return <>
      <SEOHead 
        title={pageTitle} 
        description={pageDescription} 
        canonicalUrl={canonicalUrl} 
        imageUrl={socialMediaImageUrl} 
        type="profile" 
        structuredData={structuredData} 
      />

      <div className="relative">
        {/* Warm gradient background applied to the content area */}
        <div className="absolute inset-0 -z-10">
          <div className="absolute warm-gradient"></div>
          <div className="absolute inset-0 opacity-40" style={{
          backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3z' fill='%23FFDFCE' fill-opacity='0.05' fill-rule='evenodd'/%3E%3C/svg%3E\")",
          opacity: "0.4"
        }}></div>
        </div>
        
        <div className="max-w-6xl mx-auto px-4 relative z-10 py-[48px]">
          {isSimplifiedView && <Alert variant="warning" className="mb-6">
              <Clock className="h-4 w-4" />
              <AlertDescription>
                Viewing simplified profile due to large data size. Some portfolio
                items may be limited.
              </AlertDescription>
            </Alert>}
          <ProfileHeader profile={profile} placeholderImage={placeholderImage} onOpenHireMeModal={onOpenHireMeModal} onOpenPrimaryHireMeModal={onOpenPrimaryHireMeModal} />
          <ProfileTabs profile={profile} isSimplifiedView={isSimplifiedView} openPortfolioModal={openPortfolioModal} onClosePortfolioModal={onClosePortfolioModal} />
        </div>
      </div>
    </>;
};
export default ProfilePageContent;